import { HStack, Input, Text, VStack } from '@chakra-ui/react'
import { PopUpMessageContext, ScreenContext } from '@hb/react'
import { ActionButton, PageContainer, SolidActionButton, useNavigate } from '@hb/react-web'
import { auth } from '@hb/react-web/src/backend/auth'
import { checkActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
import { useCallback, useContext, useEffect, useState } from 'react'

interface ResetPasswordViewProps {
  oobCode: string
}

export const ResetPasswordView = ({ oobCode }: ResetPasswordViewProps) => {
  const { contentWidth } = useContext(ScreenContext)
  const [loading, setLoading] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [email, setEmail] = useState('')
  const navigate = useNavigate()
  const { showError, showSuccess } = useContext(PopUpMessageContext)

  useEffect(() => {
    const loadActionCodeInfo = async () => {
      try {
        const info = await checkActionCode(auth, oobCode)
        if (info.data.email) {
          setEmail(info.data.email)
        }
      } catch (error) {
        console.error(error)
        showError('Invalid or expired password reset link')
        navigate('/')
      }
    }
    loadActionCodeInfo()
  }, [oobCode, showError, navigate])

  const handleResetPassword = useCallback(async () => {
    if (!oobCode) {
      showError('No reset code found')
      return
    }
    if (!newPassword || !confirmPassword) {
      showError('Please enter and confirm your new password')
      return
    }
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match')
      return
    }
    if (newPassword.length < 6) {
      showError('Password must be at least 6 characters')
      return
    }
    setLoading(true)
    try {
      await verifyPasswordResetCode(auth, oobCode)
      await confirmPasswordReset(auth, oobCode, newPassword)
      showSuccess('Password reset successfully. Please sign in with your new password.')
      setTimeout(() => navigate('/'), 2000)
    } catch (error) {
      console.error(error)
      showError('Error resetting password. The link may be invalid or expired.')
    } finally {
      setLoading(false)
    }
  }, [oobCode, newPassword, confirmPassword, showError, showSuccess, navigate])

  return (
    <PageContainer>
      <VStack
        flexDir="column"
        w="100%"
        width={contentWidth}
        bg="gray.50"
        py={4}
        px={6}
        alignItems="flex-start"
        spacing={0}>
        <Text fontFamily="Hero-New" fontSize="xl" fontWeight={500} color="gray.600">
          Reset Password
        </Text>
        <Text fontSize="lg" mb={2}>
          Enter a new password for {email}
        </Text>
        <VStack w="100%" spacing={3} alignItems="flex-start">
          <Input
            type="password"
            placeholder="New Password"
            colorScheme="green"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            size="lg"
          />
          <Input
            type="password"
            placeholder="Confirm Password"
            colorScheme="green"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            size="lg"
          />
        </VStack>
        <HStack w="100%" mt={3}>
          <ActionButton onClick={() => navigate('/')}>Cancel</ActionButton>
          <SolidActionButton ml="auto" onClick={handleResetPassword} isLoading={loading}>
            Reset Password
          </SolidActionButton>
        </HStack>
      </VStack>
    </PageContainer>
  )
}
